import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import LabsPanel from "../components/LabsPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Users,
  BookOpen,
  DollarSign,
  TrendingUp,
  Loader2,
  Shield,
  Search,
  UserCog,
  Trash2,
  Edit,
  Eye,
  Plus,
  BarChart3,
  Settings,
  Crown,
  GraduationCap,
  User,
  RefreshCw,
  CreditCard,
  Mail,
  KeyRound,
  Power,
  Send,
  Server,
  FileText,
  Save,
  Ban,
  CheckCircle2,
  FlaskConical,
  Tag,
  Percent,
  Calendar,
  Hash,
  Gift,
  ToggleLeft,
  Copy,
  UserPlus,
  Briefcase,
  Video,
  MessageSquare,
  Link2,
  ClipboardList,
  Sparkles,
  ExternalLink,
  PieChart,
  Banknote,
  Receipt,
  ArrowRight,
} from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";

const AdminDashboard = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Users management
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Courses management
  const [courses, setCourses] = useState([]);
  const [coursesTotal, setCoursesTotal] = useState(0);
  const [coursesPage, setCoursesPage] = useState(1);
  const [courseSearch, setCourseSearch] = useState("");
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // Selected items for editing
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [newRole, setNewRole] = useState("");

  // User details dialog
  const [detailsUser, setDetailsUser] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Send email dialog
  const [emailUser, setEmailUser] = useState(null);
  const [emailForm, setEmailForm] = useState({ template: "custom", subject: "", message: "" });
  const [sendingEmail, setSendingEmail] = useState(false);

  // Reset password result
  const [resetResult, setResetResult] = useState(null);

  // Create user dialog
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ name: "", email: "", password: "", role: "student" });
  const [creatingUser, setCreatingUser] = useState(false);

  // Promotions
  const [promotions, setPromotions] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [promoForm, setPromoForm] = useState({
    name: "", promo_type: "code", code: "", discount_percent: 10,
    is_active: true, start_date: "", end_date: "", max_uses: "", applies_to: "all",
  });
  const [creatingPromo, setCreatingPromo] = useState(false);

  // Recruitment
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [campaignForm, setCampaignForm] = useState({ domain: "", title: "", description: "", requirements: "" });
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [generatingPosts, setGeneratingPosts] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(null);
  const [candidateFilter, setCandidateFilter] = useState("");
  const [statusDialog, setStatusDialog] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "", meet_link: "", interview_date: "", admin_notes: "" });

  // Commissions
  const [commSettings, setCommSettings] = useState(null);
  const [commSummary, setCommSummary] = useState(null);
  const [loadingComm, setLoadingComm] = useState(false);
  const [savingComm, setSavingComm] = useState(false);
  const [commForm, setCommForm] = useState({ platform_percent: 30, instructor_percent: 70, vat_percent: 20, tax_percent: 0 });

  // SMTP + templates
  const [smtp, setSmtp] = useState({ host: "", port: 587, username: "", password: "", from_email: "", from_name: "DevOpsLearn", use_tls: true });
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [templates, setTemplates] = useState({});
  const [activeTemplate, setActiveTemplate] = useState("welcome");
  const [savingTemplates, setSavingTemplates] = useState(false);

  useEffect(() => {
    fetchDashboard();
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === "users") {
      fetchUsers();
    } else if (activeTab === "courses") {
      fetchCourses();
    } else if (activeTab === "emails") {
      fetchSmtp();
      fetchTemplates();
    } else if (activeTab === "promotions") {
      fetchPromotions();
    } else if (activeTab === "recruitment") {
      fetchCampaigns();
      fetchCandidates();
    } else if (activeTab === "commissions") {
      fetchCommissions();
    }
  }, [activeTab, usersPage, userSearch, userRoleFilter, coursesPage, courseSearch]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams();
      params.append("page", usersPage.toString());
      params.append("limit", "15");
      if (userSearch) params.append("search", userSearch);
      if (userRoleFilter && userRoleFilter !== "all") params.append("role", userRoleFilter);

      const response = await axios.get(`${API}/admin/users?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const params = new URLSearchParams();
      params.append("page", coursesPage.toString());
      params.append("limit", "15");
      if (courseSearch) params.append("search", courseSearch);

      const response = await axios.get(`${API}/admin/courses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourses(response.data.courses);
      setCoursesTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const updateUserRole = async () => {
    if (!selectedUser || !newRole) return;
    
    try {
      await axios.patch(
        `${API}/admin/users/${selectedUser.user_id}/role`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Rôle mis à jour: ${newRole}`);
      setSelectedUser(null);
      setNewRole("");
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Utilisateur supprimé");
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const deleteCourse = async (courseId) => {
    try {
      await axios.delete(`${API}/admin/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cours supprimé");
      fetchCourses();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  // ===== Actions avancées utilisateurs =====
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const openUserDetails = async (user) => {
    setDetailsUser(user);
    setDetailsData(null);
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/admin/users/${user.user_id}/details`, authHeaders);
      setDetailsData(res.data);
    } catch {
      toast.error("Impossible de charger les détails");
    } finally {
      setLoadingDetails(false);
    }
  };

  const toggleUserStatus = async (user) => {
    const next = !(user.is_active !== false);
    try {
      await axios.patch(`${API}/admin/users/${user.user_id}/status`, { is_active: next }, authHeaders);
      toast.success(next ? "Compte activé" : "Compte désactivé");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const resetUserPassword = async (user) => {
    try {
      const res = await axios.post(`${API}/admin/users/${user.user_id}/reset-password`, {}, authHeaders);
      setResetResult({ user, ...res.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const sendUserEmail = async () => {
    if (!emailUser) return;
    setSendingEmail(true);
    try {
      await axios.post(`${API}/admin/users/${emailUser.user_id}/send-email`, emailForm, authHeaders);
      toast.success("Email envoyé");
      setEmailUser(null);
      setEmailForm({ template: "custom", subject: "", message: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec de l'envoi (SMTP configuré ?)");
    } finally {
      setSendingEmail(false);
    }
  };

  // ===== SMTP & templates =====
  const fetchSmtp = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/smtp`, authHeaders);
      setSmtp((prev) => ({ ...prev, ...res.data }));
    } catch { /* noop */ }
  };

  const saveSmtp = async () => {
    setSavingSmtp(true);
    try {
      await axios.put(`${API}/admin/settings/smtp`, smtp, authHeaders);
      toast.success("Configuration SMTP enregistrée");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSavingSmtp(false);
    }
  };

  const testSmtp = async () => {
    if (!testEmail) { toast.error("Saisis un email de destination"); return; }
    try {
      await axios.post(`${API}/admin/settings/smtp/test`, { to_email: testEmail }, authHeaders);
      toast.success("Email de test envoyé");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Échec du test");
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`, authHeaders);
      setTemplates(res.data.templates || {});
    } catch { /* noop */ }
  };

  const saveTemplates = async () => {
    setSavingTemplates(true);
    try {
      await axios.put(`${API}/admin/email-templates`, { templates }, authHeaders);
      toast.success("Templates enregistrés");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSavingTemplates(false);
    }
  };

  const resetTemplates = async () => {
    try {
      const res = await axios.post(`${API}/admin/email-templates/reset`, {}, authHeaders);
      setTemplates(res.data.templates || {});
      toast.success("Templates réinitialisés");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  // ===== Create user (admin) =====
  const createUser = async () => {
    if (!createUserForm.name.trim() || !createUserForm.email || !createUserForm.password) {
      toast.error("Remplis tous les champs");
      return;
    }
    setCreatingUser(true);
    try {
      await axios.post(`${API}/admin/users/create`, createUserForm, authHeaders);
      toast.success("Utilisateur créé avec succès");
      setShowCreateUser(false);
      setCreateUserForm({ name: "", email: "", password: "", role: "student" });
      fetchUsers();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreatingUser(false);
    }
  };

  // ===== Promotions CRUD =====
  const fetchPromotions = async () => {
    setLoadingPromos(true);
    try {
      const res = await axios.get(`${API}/admin/promotions`, authHeaders);
      setPromotions(res.data.promotions || []);
    } catch {
      toast.error("Erreur chargement promotions");
    } finally {
      setLoadingPromos(false);
    }
  };

  const createPromotion = async () => {
    if (!promoForm.name.trim()) { toast.error("Le nom est requis"); return; }
    if (promoForm.promo_type === "code" && !promoForm.code.trim()) { toast.error("Le code promo est requis"); return; }
    setCreatingPromo(true);
    try {
      const payload = {
        ...promoForm,
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses) : null,
        start_date: promoForm.start_date || null,
        end_date: promoForm.end_date || null,
      };
      await axios.post(`${API}/admin/promotions`, payload, authHeaders);
      toast.success("Promotion créée");
      setShowCreatePromo(false);
      setPromoForm({ name: "", promo_type: "code", code: "", discount_percent: 10, is_active: true, start_date: "", end_date: "", max_uses: "", applies_to: "all" });
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setCreatingPromo(false);
    }
  };

  const togglePromoStatus = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promotions/${promo.promotion_id}`, { is_active: !promo.is_active }, authHeaders);
      toast.success(promo.is_active ? "Promotion désactivée" : "Promotion activée");
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  const deletePromotion = async (promoId) => {
    try {
      await axios.delete(`${API}/admin/promotions/${promoId}`, authHeaders);
      toast.success("Promotion supprimée");
      fetchPromotions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  // ========== RECRUITMENT FUNCTIONS ==========

  const fetchCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const res = await axios.get(`${API}/admin/recruitment/campaigns`, authHeaders);
      setCampaigns(res.data.campaigns || []);
    } catch { toast.error("Erreur chargement campagnes"); }
    finally { setLoadingCampaigns(false); }
  };

  const createCampaign = async () => {
    if (!campaignForm.domain.trim() || !campaignForm.title.trim()) {
      toast.error("Le domaine et le titre sont requis"); return;
    }
    setCreatingCampaign(true);
    try {
      await axios.post(`${API}/admin/recruitment/campaigns`, campaignForm, authHeaders);
      toast.success("Campagne créée !");
      setShowCreateCampaign(false);
      setCampaignForm({ domain: "", title: "", description: "", requirements: "" });
      fetchCampaigns();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setCreatingCampaign(false); }
  };

  const deleteCampaign = async (id) => {
    try {
      await axios.delete(`${API}/admin/recruitment/campaigns/${id}`, authHeaders);
      toast.success("Campagne supprimée");
      fetchCampaigns();
      fetchCandidates();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const generatePosts = async (campaignId) => {
    setGeneratingPosts(campaignId);
    try {
      const res = await axios.post(`${API}/admin/recruitment/ai/generate-posts`, { campaign_id: campaignId }, authHeaders);
      setCampaigns((prev) => prev.map((c) => c.campaign_id === campaignId ? { ...c, ai_posts: res.data.posts } : c));
      toast.success("Publications générées par l'IA !");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur IA"); }
    finally { setGeneratingPosts(null); }
  };

  const generateQuestions = async (campaignId) => {
    setGeneratingQuestions(campaignId);
    try {
      const res = await axios.post(`${API}/admin/recruitment/ai/generate-questions`, { campaign_id: campaignId }, authHeaders);
      setCampaigns((prev) => prev.map((c) => c.campaign_id === campaignId ? { ...c, ai_questions: res.data } : c));
      toast.success("Questions d'entretien générées !");
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur IA"); }
    finally { setGeneratingQuestions(null); }
  };

  const fetchCandidates = async (campId) => {
    setLoadingCandidates(true);
    try {
      const url = campId ? `${API}/admin/recruitment/candidates?campaign_id=${campId}` : `${API}/admin/recruitment/candidates`;
      const res = await axios.get(url, authHeaders);
      setCandidates(res.data.candidates || []);
    } catch { toast.error("Erreur chargement candidats"); }
    finally { setLoadingCandidates(false); }
  };

  const updateCandidateStatus = async () => {
    if (!statusDialog) return;
    try {
      await axios.patch(
        `${API}/admin/recruitment/candidates/${statusDialog}/status`,
        statusForm,
        authHeaders
      );
      toast.success("Statut mis à jour + notification envoyée");
      setStatusDialog(null);
      setStatusForm({ status: "", meet_link: "", interview_date: "", admin_notes: "" });
      fetchCandidates();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const validateCandidate = async (candidateId) => {
    try {
      const res = await axios.post(`${API}/admin/recruitment/candidates/${candidateId}/validate`, {}, authHeaders);
      toast.success(`Instructeur créé : ${res.data.email}`);
      fetchCandidates();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const candidateStatusBadge = (status) => {
    const map = {
      pending: { cls: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30", label: "En attente" },
      interview_scheduled: { cls: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "Entretien" },
      trial: { cls: "bg-purple-500/20 text-purple-400 border-purple-500/30", label: "Essai" },
      accepted: { cls: "bg-primary/15 text-primary border-primary/30", label: "Accepté" },
      rejected: { cls: "bg-red-500/20 text-red-400 border-red-500/30", label: "Refusé" },
    };
    const s = map[status] || { cls: "bg-muted", label: status };
    return <Badge className={s.cls}>{s.label}</Badge>;
  };

  // ========== COMMISSION FUNCTIONS ==========

  const fetchCommissions = async () => {
    setLoadingComm(true);
    try {
      const [settingsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/admin/commission/settings`, authHeaders),
        axios.get(`${API}/admin/commission/summary`, authHeaders),
      ]);
      setCommSettings(settingsRes.data);
      setCommForm({
        platform_percent: settingsRes.data.platform_percent,
        instructor_percent: settingsRes.data.instructor_percent,
        vat_percent: settingsRes.data.vat_percent,
        tax_percent: settingsRes.data.tax_percent,
      });
      setCommSummary(summaryRes.data);
    } catch { toast.error("Erreur chargement commissions"); }
    finally { setLoadingComm(false); }
  };

  const saveCommSettings = async () => {
    setSavingComm(true);
    try {
      await axios.put(`${API}/admin/commission/settings`, commForm, authHeaders);
      toast.success("Paramètres de commission mis à jour");
      fetchCommissions();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
    finally { setSavingComm(false); }
  };

  const markEarningPaid = async (earningId) => {
    try {
      await axios.patch(`${API}/admin/commission/earnings/${earningId}/status`, {}, authHeaders);
      toast.success("Gain marqué comme payé");
      fetchCommissions();
    } catch (error) { toast.error(error.response?.data?.detail || "Erreur"); }
  };

  const promoStatusBadge = (promo) => {
    const now = new Date().toISOString();
    if (!promo.is_active) return <Badge className="bg-muted text-muted-foreground border-border">Inactif</Badge>;
    if (promo.end_date && now > promo.end_date) return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Expiré</Badge>;
    if (promo.start_date && now < promo.start_date) return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Programmé</Badge>;
    if (promo.max_uses && promo.uses_count >= promo.max_uses) return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Épuisé</Badge>;
    return <Badge className="bg-primary/15 text-primary border-primary/30"><CheckCircle2 className="w-3 h-3 mr-1" /> Actif</Badge>;
  };

  const roleColors = {
    student: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    instructor: "bg-purple-500/20 text-purple-400 border-purple-500/30",
    admin: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  const roleIcons = {
    student: <GraduationCap className="w-4 h-4" />,
    instructor: <BookOpen className="w-4 h-4" />,
    admin: <Crown className="w-4 h-4" />,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const {
    total_users = 0,
    total_courses = 0,
    total_enrollments = 0,
    total_revenue = 0,
    recent_users = [],
  } = dashboardData || {};

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-red-500/20 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold">Administration</h1>
              <p className="text-muted-foreground">Contrôle total de la plateforme</p>
            </div>
          </div>
          <Button onClick={() => { fetchDashboard(); fetchStats(); }} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-7 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Vue d'ensemble</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Utilisateurs</span>
            </TabsTrigger>
            <TabsTrigger value="courses" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Cours</span>
            </TabsTrigger>
            <TabsTrigger value="labs" className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              <span className="hidden sm:inline">Labos</span>
            </TabsTrigger>
            <TabsTrigger value="promotions" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              <span className="hidden sm:inline">Promotions</span>
            </TabsTrigger>
            <TabsTrigger value="recruitment" className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Recrutement</span>
            </TabsTrigger>
            <TabsTrigger value="commissions" className="flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              <span className="hidden sm:inline">Commissions</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
            <TabsTrigger value="content" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Créer</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">{total_users}</p>
                    <p className="text-sm text-muted-foreground">Utilisateurs</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">{total_courses}</p>
                    <p className="text-sm text-muted-foreground">Cours</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">{total_enrollments}</p>
                    <p className="text-sm text-muted-foreground">Inscriptions</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-3xl font-heading font-bold">
                      ${total_revenue.toFixed(0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Revenus</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Users by Role */}
            {stats && (
              <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    Utilisateurs par rôle
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(stats.users_by_role || {}).map(([role, count]) => (
                      <div key={role} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {roleIcons[role]}
                          <span className="capitalize">{role === "student" ? "Étudiants" : role === "instructor" ? "Instructeurs" : "Admins"}</span>
                        </div>
                        <Badge className={roleColors[role]}>{count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Abonnements actifs
                  </h3>
                  <p className="text-4xl font-heading font-bold text-primary">
                    {stats.active_subscriptions || 0}
                  </p>
                  <p className="text-muted-foreground text-sm mt-1">
                    abonnements en cours
                  </p>
                </div>
              </div>
            )}

            {/* Recent Users */}
            <div className="bg-card border border-border rounded-lg">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h2 className="font-heading font-bold flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Utilisateurs récents
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setActiveTab("users")}>
                  Voir tout
                </Button>
              </div>
              
              <div className="divide-y divide-border">
                {recent_users.map((user) => (
                  <div key={user.user_id} className="p-4 flex items-center gap-4">
                    <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
                      <span className="font-bold text-sm">
                        {user.name?.charAt(0) || "U"}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{user.name}</p>
                      <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                    </div>
                    <Badge className={roleColors[user.role] || roleColors.student}>
                      {user.role}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un utilisateur..."
                  value={userSearch}
                  onChange={(e) => { setUserSearch(e.target.value); setUsersPage(1); }}
                  className="pl-10"
                  data-testid="user-search-input"
                />
              </div>
              <Select value={userRoleFilter} onValueChange={(v) => { setUserRoleFilter(v); setUsersPage(1); }}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Tous les rôles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="student">Étudiants</SelectItem>
                  <SelectItem value="instructor">Instructeurs</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
              <Button className="glow-green" onClick={() => setShowCreateUser(true)}>
                <UserPlus className="w-4 h-4 mr-2" />
                Créer
              </Button>
            </div>

            {/* Users Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loadingUsers ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-medium text-sm">Utilisateur</th>
                          <th className="text-left p-4 font-medium text-sm">Email</th>
                          <th className="text-left p-4 font-medium text-sm">Rôle</th>
                          <th className="text-left p-4 font-medium text-sm">Statut</th>
                          <th className="text-left p-4 font-medium text-sm">Date</th>
                          <th className="text-right p-4 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {users.map((user) => (
                          <tr key={user.user_id} className="hover:bg-muted/30">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                                  {user.name?.charAt(0) || "U"}
                                </div>
                                <span className="font-medium">{user.name}</span>
                              </div>
                            </td>
                            <td className="p-4 text-muted-foreground">{user.email}</td>
                            <td className="p-4">
                              <Badge className={roleColors[user.role]}>
                                {roleIcons[user.role]}
                                <span className="ml-1">{user.role}</span>
                              </Badge>
                            </td>
                            <td className="p-4">
                              {user.is_active === false ? (
                                <Badge className="bg-muted text-muted-foreground border-border">
                                  <Ban className="w-3 h-3 mr-1" /> Inactif
                                </Badge>
                              ) : (
                                <Badge className="bg-primary/15 text-primary border-primary/30">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Actif
                                </Badge>
                              )}
                            </td>
                            <td className="p-4 text-muted-foreground text-sm">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString("fr-FR") : "-"}
                            </td>
                            <td className="p-4">
                              <div className="flex justify-end gap-1 flex-wrap">
                                {/* Détails / abonnement */}
                                <Button variant="ghost" size="icon" title="Détails & abonnement"
                                  onClick={() => openUserDetails(user)}>
                                  <Eye className="w-4 h-4" />
                                </Button>

                                {/* Envoyer un email */}
                                <Button variant="ghost" size="icon" title="Envoyer un email"
                                  onClick={() => { setEmailUser(user); setEmailForm({ template: "custom", subject: "", message: "" }); }}>
                                  <Mail className="w-4 h-4" />
                                </Button>

                                {/* Réinitialiser le mot de passe */}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" title="Réinitialiser le mot de passe">
                                      <KeyRound className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Réinitialiser le mot de passe ?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Un mot de passe temporaire sera généré pour <strong>{user.name}</strong> et
                                        envoyé par email (si le SMTP est configuré). Il devra le changer ensuite.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => resetUserPassword(user)}>
                                        Réinitialiser
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>

                                {/* Activer / désactiver */}
                                <Button
                                  variant="ghost" size="icon"
                                  title={user.is_active === false ? "Activer le compte" : "Désactiver le compte"}
                                  onClick={() => toggleUserStatus(user)}
                                  className={user.is_active === false ? "text-primary" : "text-orange-400"}
                                >
                                  <Power className="w-4 h-4" />
                                </Button>

                                {/* Edit Role Dialog */}
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => { setSelectedUser(user); setNewRole(user.role); }}
                                      data-testid={`edit-user-${user.user_id}`}
                                    >
                                      <UserCog className="w-4 h-4" />
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent>
                                    <DialogHeader>
                                      <DialogTitle>Modifier le rôle</DialogTitle>
                                    </DialogHeader>
                                    <div className="py-4">
                                      <p className="text-sm text-muted-foreground mb-4">
                                        Utilisateur: <strong>{user.name}</strong> ({user.email})
                                      </p>
                                      <Select value={newRole} onValueChange={setNewRole}>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="student">
                                            <div className="flex items-center gap-2">
                                              <GraduationCap className="w-4 h-4" />
                                              Étudiant
                                            </div>
                                          </SelectItem>
                                          <SelectItem value="instructor">
                                            <div className="flex items-center gap-2">
                                              <BookOpen className="w-4 h-4" />
                                              Instructeur
                                            </div>
                                          </SelectItem>
                                          <SelectItem value="admin">
                                            <div className="flex items-center gap-2">
                                              <Crown className="w-4 h-4" />
                                              Admin
                                            </div>
                                          </SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <DialogFooter>
                                      <Button onClick={updateUserRole} data-testid="confirm-role-change">
                                        Enregistrer
                                      </Button>
                                    </DialogFooter>
                                  </DialogContent>
                                </Dialog>

                                {/* Delete User */}
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Supprimer l'utilisateur?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Cette action est irréversible. Toutes les données de {user.name} seront supprimées.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteUser(user.user_id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Supprimer
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {usersTotal} utilisateur(s) au total
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={usersPage === 1}
                        onClick={() => setUsersPage(p => p - 1)}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={users.length < 15}
                        onClick={() => setUsersPage(p => p + 1)}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Courses Tab */}
          <TabsContent value="courses" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher un cours..."
                  value={courseSearch}
                  onChange={(e) => { setCourseSearch(e.target.value); setCoursesPage(1); }}
                  className="pl-10"
                />
              </div>
              <Link to="/admin/course/new">
                <Button className="glow-green">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau cours
                </Button>
              </Link>
            </div>

            {/* Courses Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loadingCourses ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-medium text-sm">Cours</th>
                          <th className="text-left p-4 font-medium text-sm">Catégorie</th>
                          <th className="text-left p-4 font-medium text-sm">Prix</th>
                          <th className="text-left p-4 font-medium text-sm">Étudiants</th>
                          <th className="text-left p-4 font-medium text-sm">Status</th>
                          <th className="text-right p-4 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {courses.map((course) => (
                          <tr key={course.course_id} className="hover:bg-muted/30">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <img
                                  src={course.thumbnail || "https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=100"}
                                  alt={course.title}
                                  className="w-12 h-8 object-cover rounded"
                                />
                                <div>
                                  <p className="font-medium line-clamp-1">{course.title}</p>
                                  <p className="text-xs text-muted-foreground">{course.instructor_name}</p>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline">{course.category}</Badge>
                            </td>
                            <td className="p-4 font-mono">${course.price}</td>
                            <td className="p-4">{course.students_count}</td>
                            <td className="p-4">
                              {course.is_published ? (
                                <Badge className="bg-primary/20 text-primary">Publié</Badge>
                              ) : (
                                <Badge variant="outline">Brouillon</Badge>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex justify-end gap-2">
                                <Link to={`/admin/course/${course.course_id}`}>
                                  <Button variant="ghost" size="icon">
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <Link to={`/courses/${course.course_id}`}>
                                  <Button variant="ghost" size="icon">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </Link>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Supprimer le cours?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Cette action supprimera le cours "{course.title}" et tout son contenu (leçons, labs, quiz).
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deleteCourse(course.course_id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Supprimer
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="p-4 border-t border-border flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {coursesTotal} cours au total
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={coursesPage === 1}
                        onClick={() => setCoursesPage(p => p - 1)}
                      >
                        Précédent
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={courses.length < 15}
                        onClick={() => setCoursesPage(p => p + 1)}
                      >
                        Suivant
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Labs Tab : création + configuration d'environnement */}
          <TabsContent value="labs" className="space-y-6">
            <LabsPanel />
          </TabsContent>

          {/* ==================== RECRUITMENT TAB ==================== */}
          <TabsContent value="recruitment" className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-heading font-bold flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Recrutement d'instructeurs
                </h2>
                <p className="text-sm text-muted-foreground">Gérez vos campagnes de recrutement avec l'IA</p>
              </div>
              <Dialog open={showCreateCampaign} onOpenChange={setShowCreateCampaign}>
                <DialogTrigger asChild>
                  <Button className="glow-green"><Plus className="w-4 h-4 mr-2" />Nouvelle campagne</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Créer une campagne de recrutement</DialogTitle></DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div><Label>Domaine d'expertise *</Label><Input value={campaignForm.domain} onChange={(e) => setCampaignForm({ ...campaignForm, domain: e.target.value })} placeholder="Docker, Kubernetes, AWS..." /></div>
                    <div><Label>Titre du poste *</Label><Input value={campaignForm.title} onChange={(e) => setCampaignForm({ ...campaignForm, title: e.target.value })} placeholder="Formateur Docker avancé" /></div>
                    <div><Label>Description</Label><Textarea value={campaignForm.description} onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })} placeholder="Description du poste..." rows={3} /></div>
                    <div><Label>Prérequis</Label><Textarea value={campaignForm.requirements} onChange={(e) => setCampaignForm({ ...campaignForm, requirements: e.target.value })} placeholder="Certifications, expérience minimum..." rows={2} /></div>
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowCreateCampaign(false)}>Annuler</Button>
                      <Button onClick={createCampaign} disabled={creatingCampaign}>{creatingCampaign ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer"}</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Campaigns list */}
            <div className="space-y-4">
              {loadingCampaigns ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-12 bg-card border border-border rounded-lg">
                  <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune campagne. Créez-en une pour commencer.</p>
                </div>
              ) : campaigns.map((camp) => (
                <div key={camp.campaign_id} className="bg-card border border-border rounded-lg overflow-hidden">
                  <div className="p-5 flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold truncate">{camp.title}</h3>
                        <Badge className={camp.status === "active" ? "bg-primary/15 text-primary border-primary/30" : "bg-muted text-muted-foreground"}>{camp.status === "active" ? "Active" : "Fermée"}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{camp.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><GraduationCap className="w-3 h-3" />{camp.domain}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{camp.candidates_count || 0} candidat(s)</span>
                        <span>{new Date(camp.created_at).toLocaleDateString("fr-FR")}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/recruitment/apply/${camp.campaign_id}`); toast.success("Lien copié !"); }}>
                        <Link2 className="w-3.5 h-3.5 mr-1" />Lien
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-400 hover:text-red-300" onClick={() => { if (window.confirm("Supprimer cette campagne ?")) deleteCampaign(camp.campaign_id); }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* AI Actions */}
                  <div className="border-t border-border px-5 py-3 bg-muted/30 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => generatePosts(camp.campaign_id)} disabled={generatingPosts === camp.campaign_id}>
                      {generatingPosts === camp.campaign_id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5 text-primary" />}
                      Publications IA
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => generateQuestions(camp.campaign_id)} disabled={generatingQuestions === camp.campaign_id}>
                      {generatingQuestions === camp.campaign_id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ClipboardList className="w-3.5 h-3.5 mr-1.5 text-primary" />}
                      Questions entretien
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setSelectedCampaign(selectedCampaign === camp.campaign_id ? null : camp.campaign_id)}>
                      <Eye className="w-3.5 h-3.5 mr-1.5" />
                      {selectedCampaign === camp.campaign_id ? "Masquer" : "Détails"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => fetchCandidates(camp.campaign_id)}>
                      <Users className="w-3.5 h-3.5 mr-1.5" />
                      Candidats
                    </Button>
                  </div>

                  {/* Expanded details */}
                  {selectedCampaign === camp.campaign_id && (
                    <div className="border-t border-border">
                      {/* AI Posts */}
                      {camp.ai_posts && (
                        <div className="p-5 border-b border-border space-y-4">
                          <h4 className="font-bold text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-primary" />Publications générées</h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            {["linkedin", "facebook"].map((platform) => camp.ai_posts[platform] && (
                              <div key={platform} className="bg-muted/50 rounded-lg p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <Badge className="text-xs capitalize">{platform}</Badge>
                                  <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(camp.ai_posts[platform]); toast.success(`Publication ${platform} copiée !`); }}>
                                    <Copy className="w-3 h-3 mr-1" />Copier
                                  </Button>
                                </div>
                                <p className="text-xs whitespace-pre-line leading-relaxed max-h-64 overflow-y-auto">{camp.ai_posts[platform]}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* AI Questions */}
                      {camp.ai_questions?.questions && (
                        <div className="p-5 space-y-3">
                          <h4 className="font-bold text-sm flex items-center gap-2"><ClipboardList className="w-4 h-4 text-primary" />Questions d'entretien ({camp.ai_questions.questions.length})</h4>
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {camp.ai_questions.questions.map((q, i) => (
                              <div key={i} className="bg-muted/40 rounded-lg p-3 text-sm">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs font-mono text-muted-foreground">#{i + 1}</span>
                                  <Badge className="text-[10px]">{q.category}</Badge>
                                  <Badge variant="outline" className="text-[10px]">{q.difficulty}</Badge>
                                </div>
                                <p className="font-medium">{q.question}</p>
                                {q.expected_points && <p className="text-xs text-muted-foreground mt-1">💡 {q.expected_points}</p>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Candidates section */}
            <div className="bg-card border border-border rounded-lg">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-heading font-bold flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  Candidats ({candidates.length})
                </h3>
                <Button size="sm" variant="outline" onClick={() => fetchCandidates()}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />Actualiser
                </Button>
              </div>
              {loadingCandidates ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
              ) : candidates.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">Aucun candidat</div>
              ) : (
                <div className="divide-y divide-border">
                  {candidates.map((cand) => (
                    <div key={cand.candidate_id} className="p-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-medium text-sm truncate">{cand.full_name}</span>
                          {candidateStatusBadge(cand.status)}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{cand.email}</span>
                          <span>{cand.domain_expertise}</span>
                          <span>{cand.experience_years} ans</span>
                          {cand.videos?.length > 0 && (
                            <span className="flex items-center gap-1 text-primary"><Video className="w-3 h-3" />{cand.videos.length}/2 vidéos</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {/* Status change button */}
                        <Button size="sm" variant="outline" onClick={() => { setStatusDialog(cand.candidate_id); setStatusForm({ status: cand.status, meet_link: cand.meet_link || "", interview_date: cand.interview_date || "", admin_notes: cand.admin_notes || "" }); }}>
                          <Edit className="w-3 h-3 mr-1" />Statut
                        </Button>
                        {/* Validate button (only for trial candidates with videos) */}
                        {cand.status === "trial" && (cand.videos?.length || 0) >= 2 && (
                          <Button size="sm" className="bg-primary/90 hover:bg-primary" onClick={() => { if (window.confirm(`Valider ${cand.full_name} comme instructeur ?`)) validateCandidate(cand.candidate_id); }}>
                            <CheckCircle2 className="w-3 h-3 mr-1" />Valider
                          </Button>
                        )}
                        {/* View videos */}
                        {cand.videos?.length > 0 && (
                          <Button size="sm" variant="ghost" onClick={() => {
                            cand.videos.forEach((v) => window.open(`${API}${v.url}`, "_blank"));
                          }}>
                            <Video className="w-3 h-3 mr-1" />Voir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status update dialog */}
            <Dialog open={!!statusDialog} onOpenChange={(open) => { if (!open) setStatusDialog(null); }}>
              <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Modifier le statut du candidat</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Statut</Label>
                    <select className="w-full mt-1 bg-background border border-border rounded-md p-2 text-sm" value={statusForm.status} onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}>
                      <option value="pending">En attente</option>
                      <option value="interview_scheduled">Entretien programmé</option>
                      <option value="trial">Phase d'essai</option>
                      <option value="accepted">Accepté</option>
                      <option value="rejected">Refusé</option>
                    </select>
                  </div>
                  {statusForm.status === "interview_scheduled" && (
                    <>
                      <div><Label>Lien de réunion (Meet/Zoom) *</Label><Input value={statusForm.meet_link} onChange={(e) => setStatusForm({ ...statusForm, meet_link: e.target.value })} placeholder="https://meet.google.com/..." /></div>
                      <div><Label>Date de l'entretien</Label><Input type="datetime-local" value={statusForm.interview_date} onChange={(e) => setStatusForm({ ...statusForm, interview_date: e.target.value })} /></div>
                    </>
                  )}
                  <div><Label>Notes admin</Label><Textarea value={statusForm.admin_notes} onChange={(e) => setStatusForm({ ...statusForm, admin_notes: e.target.value })} placeholder="Notes internes..." rows={2} /></div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setStatusDialog(null)}>Annuler</Button>
                    <Button onClick={updateCandidateStatus}>Mettre à jour</Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* ==================== COMMISSIONS TAB ==================== */}
          <TabsContent value="commissions" className="space-y-6">
            {loadingComm ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <>
                {/* Settings */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-primary" />
                    Paramètres de commission
                  </h2>
                  <p className="text-sm text-muted-foreground mb-6">Configurez la répartition des revenus sur les ventes de cours</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-xs">Part plateforme (%)</Label>
                      <Input type="number" min={0} max={100} value={commForm.platform_percent} onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        setCommForm({ ...commForm, platform_percent: v, instructor_percent: 100 - v });
                      }} />
                    </div>
                    <div>
                      <Label className="text-xs">Part instructeur (%)</Label>
                      <Input type="number" min={0} max={100} value={commForm.instructor_percent} onChange={(e) => {
                        const v = parseFloat(e.target.value) || 0;
                        setCommForm({ ...commForm, instructor_percent: v, platform_percent: 100 - v });
                      }} />
                    </div>
                    <div>
                      <Label className="text-xs">TVA (%)</Label>
                      <Input type="number" min={0} max={100} step={0.1} value={commForm.vat_percent} onChange={(e) => setCommForm({ ...commForm, vat_percent: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label className="text-xs">Impôt (%)</Label>
                      <Input type="number" min={0} max={100} step={0.1} value={commForm.tax_percent} onChange={(e) => setCommForm({ ...commForm, tax_percent: parseFloat(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="mt-4 flex justify-end">
                    <Button onClick={saveCommSettings} disabled={savingComm}>
                      {savingComm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Sauvegarder
                    </Button>
                  </div>

                  {/* Preview calculation */}
                  <div className="mt-6 bg-muted/40 rounded-lg p-4">
                    <h4 className="font-bold text-sm mb-3">Exemple de calcul (vente à $49.99)</h4>
                    {(() => {
                      const gross = 49.99;
                      const vat = (gross * commForm.vat_percent / 100) / (1 + commForm.vat_percent / 100);
                      const tax = (gross - vat) * commForm.tax_percent / 100;
                      const net = gross - vat - tax;
                      const platf = net * commForm.platform_percent / 100;
                      const instr = net - platf;
                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                          <div className="bg-card rounded p-3 text-center border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Prix brut</p>
                            <p className="font-bold">${gross.toFixed(2)}</p>
                          </div>
                          <div className="bg-card rounded p-3 text-center border border-border">
                            <p className="text-xs text-muted-foreground mb-1">TVA</p>
                            <p className="font-bold text-orange-400">-${vat.toFixed(2)}</p>
                          </div>
                          <div className="bg-card rounded p-3 text-center border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Impôt</p>
                            <p className="font-bold text-orange-400">-${tax.toFixed(2)}</p>
                          </div>
                          <div className="bg-card rounded p-3 text-center border border-border">
                            <p className="text-xs text-muted-foreground mb-1">Plateforme ({commForm.platform_percent}%)</p>
                            <p className="font-bold text-blue-400">${platf.toFixed(2)}</p>
                          </div>
                          <div className="bg-card rounded p-3 text-center border border-primary/30">
                            <p className="text-xs text-muted-foreground mb-1">Instructeur ({commForm.instructor_percent}%)</p>
                            <p className="font-bold text-primary">${instr.toFixed(2)}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Summary */}
                {commSummary && (
                  <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-card border border-border rounded-lg p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center"><DollarSign className="w-5 h-5 text-primary" /></div>
                          <div>
                            <p className="text-xl font-heading font-bold">${(commSummary.summary.total_gross || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Revenu brut total</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-card border border-border rounded-lg p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center"><PieChart className="w-5 h-5 text-blue-400" /></div>
                          <div>
                            <p className="text-xl font-heading font-bold">${(commSummary.summary.total_platform || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Commission plateforme</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-card border border-border rounded-lg p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center"><Banknote className="w-5 h-5 text-yellow-400" /></div>
                          <div>
                            <p className="text-xl font-heading font-bold">${(commSummary.summary.total_pending || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">En attente de paiement</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-card border border-border rounded-lg p-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center"><Receipt className="w-5 h-5 text-green-400" /></div>
                          <div>
                            <p className="text-xl font-heading font-bold">${(commSummary.summary.total_paid || 0).toFixed(2)}</p>
                            <p className="text-xs text-muted-foreground">Déjà versé</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Per-instructor table */}
                    {commSummary.instructors?.length > 0 && (
                      <div className="bg-card border border-border rounded-lg">
                        <div className="p-4 border-b border-border">
                          <h3 className="font-heading font-bold flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary" />Gains par instructeur</h3>
                        </div>
                        <div className="divide-y divide-border">
                          {commSummary.instructors.map((inst) => (
                            <div key={inst.instructor_id} className="p-4 flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0">
                                <GraduationCap className="w-5 h-5 text-purple-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{inst.instructor_name}</p>
                                <p className="text-xs text-muted-foreground">{inst.instructor_email} · {inst.count} vente(s)</p>
                              </div>
                              <div className="text-right text-sm">
                                <p className="font-bold text-primary">${(inst.total_earned || 0).toFixed(2)}</p>
                                <p className="text-xs text-muted-foreground">
                                  <span className="text-yellow-400">${(inst.total_pending || 0).toFixed(2)}</span> en attente
                                  {inst.total_paid > 0 && <> · <span className="text-green-400">${(inst.total_paid || 0).toFixed(2)}</span> payé</>}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </TabsContent>

          {/* Emails Tab : SMTP + Templates */}
          <TabsContent value="emails" className="space-y-6">
            {/* SMTP config */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2">
                <Server className="w-5 h-5 text-primary" /> Serveur SMTP
              </h2>
              <p className="text-sm text-muted-foreground mb-4">
                Configure l'envoi d'emails (Gmail, SendGrid, Mailgun, OVH, etc.).
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1 block">Hôte SMTP</Label>
                  <Input value={smtp.host} onChange={(e) => setSmtp({ ...smtp, host: e.target.value })} placeholder="smtp.gmail.com" />
                </div>
                <div>
                  <Label className="mb-1 block">Port</Label>
                  <Input type="number" value={smtp.port} onChange={(e) => setSmtp({ ...smtp, port: parseInt(e.target.value) || 587 })} placeholder="587" />
                </div>
                <div>
                  <Label className="mb-1 block">Utilisateur</Label>
                  <Input value={smtp.username} onChange={(e) => setSmtp({ ...smtp, username: e.target.value })} placeholder="votre@email.com" />
                </div>
                <div>
                  <Label className="mb-1 block">Mot de passe</Label>
                  <Input type="password" value={smtp.password} onChange={(e) => setSmtp({ ...smtp, password: e.target.value })} placeholder="••••••••" />
                </div>
                <div>
                  <Label className="mb-1 block">Email expéditeur</Label>
                  <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="no-reply@devopslearn.com" />
                </div>
                <div>
                  <Label className="mb-1 block">Nom expéditeur</Label>
                  <Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="DevOpsLearn" />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-4">
                <Switch checked={smtp.use_tls} onCheckedChange={(c) => setSmtp({ ...smtp, use_tls: c })} />
                <Label>Utiliser TLS (STARTTLS) — port 587. Décoche pour SSL direct (port 465).</Label>
              </div>
              <div className="flex flex-wrap items-center gap-3 mt-6 pt-4 border-t border-border">
                <Button onClick={saveSmtp} disabled={savingSmtp}>
                  {savingSmtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
                <div className="flex items-center gap-2">
                  <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email de test" className="w-56" />
                  <Button variant="outline" onClick={testSmtp}>
                    <Send className="w-4 h-4 mr-2" /> Envoyer un test
                  </Button>
                </div>
              </div>
            </div>

            {/* Templates emails */}
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" /> Templates d'emails
                </h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={resetTemplates}>Réinitialiser</Button>
                  <Button size="sm" onClick={saveTemplates} disabled={savingTemplates}>
                    {savingTemplates ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Enregistrer
                  </Button>
                </div>
              </div>

              {/* Sélecteur de template */}
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.keys(templates).map((key) => {
                  const labels = { welcome: "Bienvenue", subscription: "Abonnement", reminder: "Relance", notification: "Notification", password_reset: "Reset mot de passe" };
                  return (
                    <Button key={key} size="sm"
                      variant={activeTemplate === key ? "default" : "outline"}
                      onClick={() => setActiveTemplate(key)}>
                      {labels[key] || key}
                    </Button>
                  );
                })}
              </div>

              {templates[activeTemplate] && (
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="mb-1 block">Objet</Label>
                      <Input
                        value={templates[activeTemplate].subject || ""}
                        onChange={(e) => setTemplates({ ...templates, [activeTemplate]: { ...templates[activeTemplate], subject: e.target.value } })}
                      />
                    </div>
                    <div>
                      <Label className="mb-1 block">Contenu HTML</Label>
                      <Textarea
                        rows={16}
                        className="font-mono text-xs"
                        value={templates[activeTemplate].html || ""}
                        onChange={(e) => setTemplates({ ...templates, [activeTemplate]: { ...templates[activeTemplate], html: e.target.value } })}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Variables disponibles : <code>{"{{name}}"}</code>, <code>{"{{email}}"}</code>,{" "}
                      <code>{"{{temp_password}}"}</code>, <code>{"{{plan}}"}</code>, <code>{"{{message}}"}</code>
                    </p>
                  </div>
                  {/* Aperçu */}
                  <div>
                    <Label className="mb-1 block">Aperçu</Label>
                    <iframe
                      title="tpl-preview"
                      srcDoc={templates[activeTemplate].html || ""}
                      className="w-full rounded-lg border border-border bg-white"
                      style={{ height: "420px" }}
                    />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Promotions Tab */}
          <TabsContent value="promotions" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div>
                <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                  <Tag className="w-5 h-5 text-primary" /> Gestion des promotions
                </h2>
                <p className="text-sm text-muted-foreground">Codes promo et promotions générales</p>
              </div>
              <Button className="glow-green" onClick={() => setShowCreatePromo(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer une promotion
              </Button>
            </div>

            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {loadingPromos ? (
                <div className="p-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                </div>
              ) : promotions.length === 0 ? (
                <div className="p-12 text-center">
                  <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune promotion créée</p>
                  <p className="text-sm text-muted-foreground mt-1">Créez votre première promotion pour offrir des réductions</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted/50 border-b border-border">
                        <tr>
                          <th className="text-left p-4 font-medium text-sm">Promotion</th>
                          <th className="text-left p-4 font-medium text-sm">Type</th>
                          <th className="text-left p-4 font-medium text-sm">Code</th>
                          <th className="text-left p-4 font-medium text-sm">Réduction</th>
                          <th className="text-left p-4 font-medium text-sm">Statut</th>
                          <th className="text-left p-4 font-medium text-sm">Utilisations</th>
                          <th className="text-left p-4 font-medium text-sm">Période</th>
                          <th className="text-right p-4 font-medium text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {promotions.map((promo) => (
                          <tr key={promo.promotion_id} className="hover:bg-muted/30">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${promo.promo_type === "general" ? "bg-purple-500/20" : "bg-primary/20"}`}>
                                  {promo.promo_type === "general" ? <Gift className="w-4 h-4 text-purple-400" /> : <Hash className="w-4 h-4 text-primary" />}
                                </div>
                                <span className="font-medium">{promo.name}</span>
                              </div>
                            </td>
                            <td className="p-4">
                              <Badge className={promo.promo_type === "general" ? "bg-purple-500/20 text-purple-400 border-purple-500/30" : "bg-primary/20 text-primary border-primary/30"}>
                                {promo.promo_type === "general" ? "Générale" : "Code promo"}
                              </Badge>
                            </td>
                            <td className="p-4">
                              {promo.code ? (
                                <div className="flex items-center gap-2">
                                  <code className="bg-muted px-2 py-1 rounded text-xs font-bold">{promo.code}</code>
                                  <Button variant="ghost" size="icon" className="h-6 w-6"
                                    onClick={() => { navigator.clipboard.writeText(promo.code); toast.success("Code copié !"); }}>
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">—</span>
                              )}
                            </td>
                            <td className="p-4">
                              <span className="font-bold text-lg text-primary">-{promo.discount_percent}%</span>
                            </td>
                            <td className="p-4">
                              {promoStatusBadge(promo)}
                            </td>
                            <td className="p-4 text-sm">
                              <span className="font-mono">{promo.uses_count || 0}</span>
                              {promo.max_uses && <span className="text-muted-foreground"> / {promo.max_uses}</span>}
                            </td>
                            <td className="p-4 text-sm text-muted-foreground">
                              {promo.start_date ? new Date(promo.start_date).toLocaleDateString("fr-FR") : "—"}
                              {" → "}
                              {promo.end_date ? new Date(promo.end_date).toLocaleDateString("fr-FR") : "∞"}
                            </td>
                            <td className="p-4">
                              <div className="flex justify-end gap-1">
                                <Button variant="ghost" size="icon" title={promo.is_active ? "Désactiver" : "Activer"}
                                  onClick={() => togglePromoStatus(promo)}
                                  className={promo.is_active ? "text-orange-400" : "text-primary"}
                                >
                                  <ToggleLeft className="w-4 h-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button variant="ghost" size="icon" className="text-destructive hover:text-destructive">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Supprimer la promotion ?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        La promotion "{promo.name}" sera définitivement supprimée.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Annuler</AlertDialogCancel>
                                      <AlertDialogAction
                                        onClick={() => deletePromotion(promo.promotion_id)}
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                      >
                                        Supprimer
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </TabsContent>

          {/* Content Creation Tab */}
          <TabsContent value="content" className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/admin/course/new">
                <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors cursor-pointer h-full">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                    <BookOpen className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">Créer un cours</h3>
                  <p className="text-sm text-muted-foreground">
                    Créez un nouveau cours avec vidéos, leçons et quiz
                  </p>
                </div>
              </Link>

              <Link to="/admin/labs">
                <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors cursor-pointer h-full">
                  <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                    <FlaskConical className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">Labos interactifs</h3>
                  <p className="text-sm text-muted-foreground">
                    Créez et publiez des labos autonomes (Docker, K8s, Linux…)
                  </p>
                </div>
              </Link>

              <Link to="/admin/payments">
                <div className="bg-card border border-border rounded-lg p-6 hover:border-green-500/50 transition-colors cursor-pointer h-full">
                  <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center mb-4">
                    <CreditCard className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="font-heading font-bold text-lg mb-2">Paiements & Factures</h3>
                  <p className="text-sm text-muted-foreground">
                    Gérez les transactions, factures et configuration Stripe
                  </p>
                </div>
              </Link>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="w-12 h-12 bg-secondary/20 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">Gérer les utilisateurs</h3>
                <p className="text-sm text-muted-foreground">
                  Modifiez les rôles et gérez les accès
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={() => setActiveTab("users")}
                >
                  Aller aux utilisateurs
                </Button>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="w-12 h-12 bg-accent/20 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-heading font-bold text-lg mb-2">Voir les statistiques</h3>
                <p className="text-sm text-muted-foreground">
                  Analysez les performances de la plateforme
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={() => setActiveTab("overview")}
                >
                  Voir les stats
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ===== Dialog : Détails utilisateur & abonnement ===== */}
      <Dialog open={!!detailsUser} onOpenChange={(o) => { if (!o) { setDetailsUser(null); setDetailsData(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" /> {detailsUser?.name}
            </DialogTitle>
          </DialogHeader>
          {loadingDetails ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
          ) : detailsData ? (
            <div className="space-y-4 py-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs">Email</p>
                  <p className="font-medium break-all">{detailsData.user.email}</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs">Rôle</p>
                  <p className="font-medium">{detailsData.user.role}</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs">Statut</p>
                  <p className="font-medium">{detailsData.user.is_active === false ? "Inactif" : "Actif"}</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs">Leçons complétées</p>
                  <p className="font-medium">{detailsData.lessons_completed}</p>
                </div>
              </div>

              <div className="border border-border rounded-lg p-3">
                <p className="font-semibold mb-1 flex items-center gap-2"><Crown className="w-4 h-4 text-primary" /> Abonnement</p>
                {detailsData.active_subscription ? (
                  <div className="text-muted-foreground">
                    Plan <strong className="text-foreground">{detailsData.active_subscription.plan}</strong> — actif
                    {detailsData.active_subscription.expires_at && (
                      <> jusqu'au {new Date(detailsData.active_subscription.expires_at).toLocaleDateString("fr-FR")}</>
                    )}
                  </div>
                ) : (
                  <p className="text-muted-foreground">Aucun abonnement actif</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs">Inscriptions cours</p>
                  <p className="font-medium">{detailsData.enrollments_count}</p>
                </div>
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-muted-foreground text-xs">Paiements</p>
                  <p className="font-medium">{detailsData.payments?.length || 0}</p>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ===== Dialog : Envoyer un email ===== */}
      <Dialog open={!!emailUser} onOpenChange={(o) => { if (!o) setEmailUser(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" /> Email à {emailUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block">Modèle</Label>
              <Select value={emailForm.template} onValueChange={(v) => setEmailForm({ ...emailForm, template: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Message personnalisé</SelectItem>
                  <SelectItem value="welcome">Bienvenue</SelectItem>
                  <SelectItem value="subscription">Abonnement</SelectItem>
                  <SelectItem value="reminder">Relance</SelectItem>
                  <SelectItem value="notification">Notification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {emailForm.template === "custom" && (
              <div>
                <Label className="mb-1 block">Objet</Label>
                <Input value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} placeholder="Objet de l'email" />
              </div>
            )}
            {(emailForm.template === "custom" || emailForm.template === "notification") && (
              <div>
                <Label className="mb-1 block">Message</Label>
                <Textarea rows={5} value={emailForm.message} onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })} placeholder="Votre message..." />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Nécessite un serveur SMTP configuré (onglet Emails).
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailUser(null)}>Annuler</Button>
            <Button onClick={sendUserEmail} disabled={sendingEmail}>
              {sendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog : Résultat réinitialisation mot de passe ===== */}
      <Dialog open={!!resetResult} onOpenChange={(o) => { if (!o) setResetResult(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-primary" /> Mot de passe réinitialisé
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2 text-sm">
            <p>Nouveau mot de passe temporaire pour <strong>{resetResult?.user?.name}</strong> :</p>
            <p className="text-lg font-mono font-bold text-center bg-muted/50 border border-dashed border-primary rounded-lg py-3 text-primary">
              {resetResult?.temp_password}
            </p>
            {resetResult?.email_sent ? (
              <p className="text-primary flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Envoyé par email à l'utilisateur.</p>
            ) : (
              <p className="text-orange-400">
                ⚠️ Email non envoyé (SMTP non configuré). Communique ce mot de passe manuellement.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setResetResult(null)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog : Cr\u00e9er un utilisateur ===== */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" /> Cr&eacute;er un utilisateur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block">Nom complet</Label>
              <Input value={createUserForm.name}
                onChange={(e) => setCreateUserForm({ ...createUserForm, name: e.target.value })}
                placeholder="Jean Dupont" />
            </div>
            <div>
              <Label className="mb-1 block">Email</Label>
              <Input type="email" value={createUserForm.email}
                onChange={(e) => setCreateUserForm({ ...createUserForm, email: e.target.value })}
                placeholder="jean@example.com" />
            </div>
            <div>
              <Label className="mb-1 block">Mot de passe (min. 8 caract&egrave;res)</Label>
              <Input type="password" value={createUserForm.password}
                onChange={(e) => setCreateUserForm({ ...createUserForm, password: e.target.value })}
                placeholder="••••••••" />
            </div>
            <div>
              <Label className="mb-1 block">R&ocirc;le</Label>
              <Select value={createUserForm.role} onValueChange={(v) => setCreateUserForm({ ...createUserForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="student">
                    <div className="flex items-center gap-2"><GraduationCap className="w-4 h-4" /> &Eacute;tudiant</div>
                  </SelectItem>
                  <SelectItem value="instructor">
                    <div className="flex items-center gap-2"><BookOpen className="w-4 h-4" /> Instructeur</div>
                  </SelectItem>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2"><Crown className="w-4 h-4" /> Admin</div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Annuler</Button>
            <Button onClick={createUser} disabled={creatingUser}>
              {creatingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Cr&eacute;er
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Dialog : Cr\u00e9er une promotion ===== */}
      <Dialog open={showCreatePromo} onOpenChange={setShowCreatePromo}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" /> Nouvelle promotion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block">Nom de la promotion</Label>
              <Input value={promoForm.name}
                onChange={(e) => setPromoForm({ ...promoForm, name: e.target.value })}
                placeholder="Soldes d'&eacute;t&eacute; 2026" />
            </div>
            <div>
              <Label className="mb-1 block">Type</Label>
              <Select value={promoForm.promo_type} onValueChange={(v) => setPromoForm({ ...promoForm, promo_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="code">
                    <div className="flex items-center gap-2"><Hash className="w-4 h-4" /> Code promo</div>
                  </SelectItem>
                  <SelectItem value="general">
                    <div className="flex items-center gap-2"><Gift className="w-4 h-4" /> Promotion g&eacute;n&eacute;rale</div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {promoForm.promo_type === "general" && (
                <p className="text-xs text-muted-foreground mt-1">
                  Une seule promotion g&eacute;n&eacute;rale peut &ecirc;tre active. Activer celle-ci d&eacute;sactivera les autres.
                </p>
              )}
            </div>
            {promoForm.promo_type === "code" && (
              <div>
                <Label className="mb-1 block">Code promo</Label>
                <Input value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="DEVOPS2026" className="font-mono uppercase" />
              </div>
            )}
            <div>
              <Label className="mb-1 block">Pourcentage de r&eacute;duction</Label>
              <div className="flex items-center gap-3">
                <Input type="number" min={1} max={100} value={promoForm.discount_percent}
                  onChange={(e) => setPromoForm({ ...promoForm, discount_percent: parseInt(e.target.value) || 0 })}
                  className="w-24" />
                <Percent className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">de r&eacute;duction</span>
              </div>
            </div>
            <div>
              <Label className="mb-1 block">S'applique &agrave;</Label>
              <Select value={promoForm.applies_to} onValueChange={(v) => setPromoForm({ ...promoForm, applies_to: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout (cours + abonnements)</SelectItem>
                  <SelectItem value="courses">Cours uniquement</SelectItem>
                  <SelectItem value="subscriptions">Abonnements uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block">Date de d&eacute;but</Label>
                <Input type="date" value={promoForm.start_date}
                  onChange={(e) => setPromoForm({ ...promoForm, start_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
              </div>
              <div>
                <Label className="mb-1 block">Date de fin</Label>
                <Input type="date" value={promoForm.end_date ? promoForm.end_date.split("T")[0] : ""}
                  onChange={(e) => setPromoForm({ ...promoForm, end_date: e.target.value ? new Date(e.target.value + "T23:59:59Z").toISOString() : "" })} />
              </div>
            </div>
            <div>
              <Label className="mb-1 block">Nombre max d'utilisations (vide = illimit&eacute;)</Label>
              <Input type="number" min={1} value={promoForm.max_uses}
                onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
                placeholder="Illimit\u00e9" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreatePromo(false)}>Annuler</Button>
            <Button onClick={createPromotion} disabled={creatingPromo}>
              {creatingPromo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
              Cr&eacute;er la promotion
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboard;
